import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../App';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import {
  Sparkles, Globe, FileUp, Wand2, ArrowLeft, FileText, Tag,
  Check, Loader2, Trash2, Eye, X, Wand, Rocket, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { useDropzone } from 'react-dropzone';

const AIStudio = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('help-center');

  // URL tab
  const [url, setUrl] = useState('');
  const [maxPages, setMaxPages] = useState(1);
  const [urlArticleCount, setUrlArticleCount] = useState(15);

  // File tab
  const [file, setFile] = useState(null);
  const [fileArticleCount, setFileArticleCount] = useState(15);

  // Prompt tab
  const [topic, setTopic] = useState('');
  const [promptContext, setPromptContext] = useState('');

  // Help Center tab
  const [helpCenterTopic, setHelpCenterTopic] = useState('');
  const [helpCenterCompany, setHelpCenterCompany] = useState('');
  const [helpCenterCount, setHelpCenterCount] = useState(30);
  const [helpCenterColor, setHelpCenterColor] = useState('');
  const [helpCenterRunning, setHelpCenterRunning] = useState(false);
  const [helpCenterResult, setHelpCenterResult] = useState(null);

  // Shared
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState('');
  const [categories, setCategories] = useState([]);

  // Generation state
  const [generating, setGenerating] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [previewDraft, setPreviewDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const onDrop = (accepted) => setFile(accepted[0] || null);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt'],
      'text/markdown': ['.md'],
    },
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/categories`);
      setCategories(res.data || []);
    } catch (e) {
      // ignore - existing categories endpoint may fail
      void e;
    }
  };

  const tagList = () => tags.split(',').map(t => t.trim()).filter(Boolean);

  const handleGenerateFromURL = async () => {
    if (!url.trim()) { toast.error('Please enter a URL'); return; }
    setGenerating(true); setDrafts([]); setSelectedIds(new Set());
    try {
      const res = await axios.post(`${API_URL}/ai-studio/generate-from-url`, {
        url: url.trim(),
        max_pages: parseInt(maxPages),
        target_article_count: parseInt(urlArticleCount),
        default_category: category || null,
        tags: tagList(),
      }, { timeout: 180000 });
      setDrafts(res.data.drafts || []);
      setSelectedIds(new Set((res.data.drafts || []).map(d => d.id)));
      toast.success(`Generated ${res.data.drafts.length} article${res.data.drafts.length === 1 ? '' : 's'} from ${res.data.pages_processed} page${res.data.pages_processed === 1 ? '' : 's'}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateFromFile = async () => {
    if (!file) { toast.error('Please upload a file'); return; }
    setGenerating(true); setDrafts([]); setSelectedIds(new Set());
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('target_article_count', String(fileArticleCount));
      if (category) fd.append('default_category', category);
      if (tags) fd.append('tags', tags);
      const res = await axios.post(`${API_URL}/ai-studio/generate-from-file`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
      });
      setDrafts(res.data.drafts || []);
      setSelectedIds(new Set((res.data.drafts || []).map(d => d.id)));
      toast.success(`Generated ${res.data.drafts.length} article${res.data.drafts.length === 1 ? '' : 's'}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateFromPrompt = async () => {
    if (!topic.trim()) { toast.error('Please enter a topic'); return; }
    setGenerating(true); setDrafts([]); setSelectedIds(new Set());
    try {
      const res = await axios.post(`${API_URL}/ai-studio/generate-from-prompt`, {
        topic: topic.trim(),
        additional_context: promptContext.trim(),
        category: category || null,
        tags: tagList(),
      }, { timeout: 120000 });
      setDrafts(res.data.drafts || []);
      setSelectedIds(new Set((res.data.drafts || []).map(d => d.id)));
      toast.success('Article drafted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const selectAll = () => setSelectedIds(new Set(drafts.map(d => d.id)));
  const clearAll = () => setSelectedIds(new Set());
  const removeDraft = (id) => {
    setDrafts(prev => prev.filter(d => d.id !== id));
    const next = new Set(selectedIds); next.delete(id); setSelectedIds(next);
  };

  const updateDraftField = (id, field, value) => {
    setDrafts(prev => prev.map(d => d.id === id ? { ...d, [field]: value } : d));
  };

  const handleGenerateHelpCenter = async () => {
    if (!helpCenterTopic.trim()) { toast.error('Please describe your help center'); return; }
    setHelpCenterRunning(true);
    try {
      const res = await axios.post(`${API_URL}/ai-studio/generate-help-center`, {
        topic: helpCenterTopic.trim(),
        company_name: helpCenterCompany.trim() || null,
        target_article_count: parseInt(helpCenterCount) || 30,
        primary_color: helpCenterColor || null,
        language: 'English',
      }, { timeout: 240000 });
      setHelpCenterResult(res.data);
      toast.success('Help center built!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Help center generation failed');
    } finally {
      setHelpCenterRunning(false);
    }
  };

  const handleSave = async () => {
    if (selectedIds.size === 0) { toast.error('Select at least one article to save'); return; }
    setSaving(true);
    try {
      const selected = drafts.filter(d => selectedIds.has(d.id));
      const res = await axios.post(`${API_URL}/ai-studio/save-drafts`, {
        drafts: selected,
        visibility: 'public',
      });
      toast.success(`Saved ${res.data.created} article${res.data.created === 1 ? '' : 's'} to Helpdesk`);
      navigate('/documents');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout user={user} setUser={setUser}>
      <div className="p-8 max-w-7xl mx-auto" data-testid="ai-studio-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-gradient-to-br from-violet-500 to-fuchsia-500">
                <Sparkles className="h-5 w-5 text-white" />
              </div>
              <h1 className="text-4xl font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em' }}>
                AI Studio
              </h1>
            </div>
            <p className="text-sm text-zinc-600 ml-13">
              Generate a complete Gleap-style help center — articles, news, roadmap, forms, brand &amp; chatbot persona — from one sentence, a URL, a document, or a single topic.
            </p>
          </div>
          <Button variant="outline" onClick={() => navigate('/documents')} data-testid="back-to-docs-button">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Helpdesk
          </Button>
        </div>

        {drafts.length === 0 ? (
          <div className="bg-white border border-zinc-200 rounded-2xl shadow-sm p-8">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid grid-cols-4 max-w-xl mb-6">
                <TabsTrigger value="help-center" data-testid="tab-help-center"><Rocket className="h-4 w-4 mr-2" />Full Help Center</TabsTrigger>
                <TabsTrigger value="url" data-testid="tab-url"><Globe className="h-4 w-4 mr-2" />From URL</TabsTrigger>
                <TabsTrigger value="file" data-testid="tab-file"><FileUp className="h-4 w-4 mr-2" />From File</TabsTrigger>
                <TabsTrigger value="prompt" data-testid="tab-prompt"><Wand2 className="h-4 w-4 mr-2" />From Prompt</TabsTrigger>
              </TabsList>

              {/* Help Center Tab */}
              <TabsContent value="help-center" className="space-y-4">
                <div className="rounded-2xl border-2 border-violet-200 bg-gradient-to-br from-violet-50 to-fuchsia-50 p-5">
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center flex-shrink-0">
                      <Rocket className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-zinc-900">One-click Help Center</h3>
                      <p className="text-sm text-zinc-600 mt-0.5">From a single prompt we&apos;ll scaffold articles, categories, a welcome announcement, starter roadmap, contact/bug/feature forms, and apply a brand color + chatbot persona to your workspace.</p>
                    </div>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Describe your help center</Label>
                  <Textarea
                    value={helpCenterTopic}
                    onChange={e => setHelpCenterTopic(e.target.value)}
                    placeholder="e.g. SaaS onboarding helpdesk for accounting software targeting small businesses"
                    rows={3}
                    className="mt-1.5"
                    data-testid="help-center-topic-input"
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-sm font-medium">Company name (optional)</Label>
                    <Input
                      value={helpCenterCompany}
                      onChange={e => setHelpCenterCompany(e.target.value)}
                      placeholder="Acme Co."
                      className="mt-1.5"
                      data-testid="help-center-company-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Articles to generate</Label>
                    <Input
                      type="number"
                      min={10}
                      max={50}
                      value={helpCenterCount}
                      onChange={e => setHelpCenterCount(e.target.value)}
                      className="mt-1.5"
                      data-testid="help-center-count-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Brand color (optional)</Label>
                    <div className="flex gap-2 mt-1.5">
                      <input
                        type="color"
                        value={helpCenterColor || '#7c3aed'}
                        onChange={e => setHelpCenterColor(e.target.value)}
                        className="h-10 w-16 rounded border"
                        data-testid="help-center-color-input"
                      />
                      <Input
                        value={helpCenterColor}
                        onChange={e => setHelpCenterColor(e.target.value)}
                        placeholder="Let AI decide"
                      />
                    </div>
                  </div>
                </div>
                <Button
                  onClick={handleGenerateHelpCenter}
                  disabled={helpCenterRunning}
                  className="w-full h-14 text-sm font-semibold bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:opacity-90 text-white shadow-lg"
                  data-testid="help-center-generate-button"
                >
                  {helpCenterRunning ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" />Building your help center…</> : <><Rocket className="h-5 w-5 mr-2" />Generate Full Help Center</>}
                </Button>
                <p className="text-xs text-zinc-500 text-center">⚡ Takes about 60-120 seconds. Existing categories/forms are preserved (deduped by name/slug).</p>
              </TabsContent>

              {/* URL Tab */}
              <TabsContent value="url" className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">Website URL</Label>
                  <Input
                    value={url}
                    onChange={e => setUrl(e.target.value)}
                    placeholder="https://help.example.com or https://docs.example.com/getting-started"
                    className="mt-1.5"
                    data-testid="url-input"
                  />
                  <p className="text-xs text-zinc-500 mt-1">Point to a help center, docs site, or a single article URL.</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium">Pages to crawl</Label>
                    <Input
                      type="number"
                      min={1}
                      max={50}
                      value={maxPages}
                      onChange={e => setMaxPages(e.target.value)}
                      className="mt-1.5"
                      data-testid="max-pages-input"
                    />
                    <p className="text-xs text-zinc-500 mt-1">1 = single page. Up to 50 for whole sites.</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Target article count</Label>
                    <Input
                      type="number"
                      min={1}
                      max={50}
                      value={urlArticleCount}
                      onChange={e => setUrlArticleCount(e.target.value)}
                      className="mt-1.5"
                      data-testid="url-article-count"
                    />
                    <p className="text-xs text-zinc-500 mt-1">AI will split content into this many focused articles.</p>
                  </div>
                </div>
                {renderSharedFields(category, setCategory, tags, setTags, categories)}
                <Button
                  onClick={handleGenerateFromURL}
                  disabled={generating}
                  className="w-full h-12 text-sm font-semibold bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:opacity-90"
                  data-testid="generate-url-button"
                >
                  {generating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Crawling & generating articles…</> : <><Sparkles className="h-4 w-4 mr-2" />Generate Helpdesk from URL</>}
                </Button>
              </TabsContent>

              {/* File Tab */}
              <TabsContent value="file" className="space-y-4">
                <div
                  {...getRootProps()}
                  className={`border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-colors ${
                    isDragActive ? 'border-violet-500 bg-violet-50' : 'border-zinc-300 hover:border-violet-400 bg-zinc-50'
                  }`}
                  data-testid="file-dropzone"
                >
                  <input {...getInputProps()} />
                  <FileUp className="h-10 w-10 mx-auto mb-3 text-zinc-400" />
                  {file ? (
                    <div>
                      <p className="font-medium text-zinc-900">{file.name}</p>
                      <p className="text-xs text-zinc-500 mt-1">{(file.size / 1024).toFixed(1)} KB · click to change</p>
                    </div>
                  ) : (
                    <div>
                      <p className="font-medium text-zinc-700">Drop PDF, DOCX, TXT, or MD</p>
                      <p className="text-xs text-zinc-500 mt-1">A 50-page handbook can yield 40+ helpdesk articles</p>
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-sm font-medium">Target article count</Label>
                  <Input
                    type="number"
                    min={1}
                    max={50}
                    value={fileArticleCount}
                    onChange={e => setFileArticleCount(e.target.value)}
                    className="mt-1.5"
                    data-testid="file-article-count"
                  />
                </div>
                {renderSharedFields(category, setCategory, tags, setTags, categories)}
                <Button
                  onClick={handleGenerateFromFile}
                  disabled={generating || !file}
                  className="w-full h-12 text-sm font-semibold bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:opacity-90"
                  data-testid="generate-file-button"
                >
                  {generating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating articles…</> : <><Sparkles className="h-4 w-4 mr-2" />Generate Helpdesk from File</>}
                </Button>
              </TabsContent>

              {/* Prompt Tab */}
              <TabsContent value="prompt" className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">Topic</Label>
                  <Input
                    value={topic}
                    onChange={e => setTopic(e.target.value)}
                    placeholder="e.g. How to reset a forgotten password"
                    className="mt-1.5"
                    data-testid="topic-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Additional context (optional)</Label>
                  <Textarea
                    value={promptContext}
                    onChange={e => setPromptContext(e.target.value)}
                    placeholder="Any details, constraints, screenshots descriptions, or terminology to use…"
                    rows={4}
                    className="mt-1.5"
                    data-testid="prompt-context-input"
                  />
                </div>
                {renderSharedFields(category, setCategory, tags, setTags, categories)}
                <Button
                  onClick={handleGenerateFromPrompt}
                  disabled={generating}
                  className="w-full h-12 text-sm font-semibold bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:opacity-90"
                  data-testid="generate-prompt-button"
                >
                  {generating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Drafting article…</> : <><Wand className="h-4 w-4 mr-2" />Draft Article</>}
                </Button>
              </TabsContent>
            </Tabs>
          </div>
        ) : (
          /* Draft Review */
          <div data-testid="drafts-review">
            <div className="bg-white border border-zinc-200 rounded-2xl shadow-sm p-5 mb-4 flex items-center justify-between sticky top-0 z-10">
              <div className="flex items-center gap-3">
                <div className="text-sm font-semibold text-zinc-900">
                  {selectedIds.size} of {drafts.length} selected
                </div>
                <button onClick={selectAll} className="text-xs text-violet-600 hover:text-violet-700">Select all</button>
                <button onClick={clearAll} className="text-xs text-zinc-500 hover:text-zinc-700">Clear</button>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => { setDrafts([]); setSelectedIds(new Set()); }} data-testid="discard-drafts-button">
                  Start over
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving || selectedIds.size === 0}
                  className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:opacity-90"
                  data-testid="save-drafts-button"
                >
                  {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…</> : <><Check className="h-4 w-4 mr-2" />Publish {selectedIds.size} to Helpdesk</>}
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {drafts.map(draft => (
                <div
                  key={draft.id}
                  className={`bg-white border-2 rounded-2xl p-5 transition-all ${
                    selectedIds.has(draft.id) ? 'border-violet-400 shadow-sm' : 'border-zinc-200'
                  }`}
                  data-testid={`draft-card-${draft.id}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <Checkbox
                        checked={selectedIds.has(draft.id)}
                        onCheckedChange={() => toggleSelect(draft.id)}
                        className="mt-1"
                        data-testid={`draft-checkbox-${draft.id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <input
                          value={draft.title}
                          onChange={e => updateDraftField(draft.id, 'title', e.target.value)}
                          className="w-full text-lg font-bold text-zinc-900 bg-transparent border-0 border-b border-transparent hover:border-zinc-200 focus:border-violet-400 outline-none px-0"
                          style={{ fontFamily: 'Manrope, sans-serif' }}
                        />
                        {draft.summary && (
                          <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{draft.summary}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => setPreviewDraft(draft)}
                        className="p-1.5 hover:bg-zinc-100 rounded text-zinc-500"
                        title="Preview"
                        data-testid={`preview-draft-${draft.id}`}
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => removeDraft(draft.id)}
                        className="p-1.5 hover:bg-red-50 rounded text-red-500"
                        title="Discard"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <input
                      value={draft.category || ''}
                      onChange={e => updateDraftField(draft.id, 'category', e.target.value)}
                      placeholder="Category"
                      className="px-2 py-1 text-xs rounded-md bg-violet-50 text-violet-700 border-0 outline-none w-auto min-w-[80px] font-medium"
                    />
                    {(draft.tags || []).slice(0, 4).map((tag, i) => (
                      <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-zinc-100 text-zinc-600 rounded text-xs">
                        <Tag className="h-2.5 w-2.5" />{tag}
                      </span>
                    ))}
                    {(draft.tags || []).length > 4 && (
                      <span className="text-xs text-zinc-400">+{draft.tags.length - 4}</span>
                    )}
                  </div>

                  <div
                    className="mt-3 prose prose-sm prose-zinc max-w-none text-zinc-600 line-clamp-4"
                    dangerouslySetInnerHTML={{ __html: draft.content }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Preview Dialog */}
        <Dialog open={!!previewDraft} onOpenChange={(open) => !open && setPreviewDraft(null)}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>{previewDraft?.title}</DialogTitle>
              <DialogDescription>
                {previewDraft?.category && <span className="inline-block px-2 py-0.5 bg-violet-50 text-violet-700 rounded text-xs font-medium mr-2">{previewDraft.category}</span>}
                {previewDraft?.summary}
              </DialogDescription>
            </DialogHeader>
            <div className="overflow-y-auto flex-1">
              <article
                className="prose prose-zinc max-w-none"
                dangerouslySetInnerHTML={{ __html: previewDraft?.content || '' }}
              />
            </div>
          </DialogContent>
        </Dialog>

        {/* Help Center Success Dialog */}
        <Dialog open={!!helpCenterResult} onOpenChange={(open) => !open && setHelpCenterResult(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
                  <Rocket className="h-5 w-5 text-white" />
                </div>
                Your help center is live!
              </DialogTitle>
              <DialogDescription>Everything below was generated and applied to your workspace.</DialogDescription>
            </DialogHeader>
            {helpCenterResult && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <Stat label="Articles" value={helpCenterResult.articles_created} />
                  <Stat label="Categories" value={helpCenterResult.categories_created} />
                  <Stat label="News posts" value={helpCenterResult.news_created} />
                  <Stat label="Roadmap items" value={helpCenterResult.roadmap_created} />
                  <Stat label="Forms" value={helpCenterResult.forms_created} />
                  <Stat label="Brand color" value={helpCenterResult.theme?.primary_color || '—'} />
                </div>
                {helpCenterResult.theme && (
                  <div className="bg-gradient-to-br p-4 rounded-xl text-white" style={{ background: `linear-gradient(135deg, ${helpCenterResult.theme.primary_color || '#7c3aed'} 0%, ${helpCenterResult.theme.primary_color || '#7c3aed'}cc 100%)` }}>
                    <div className="text-xs uppercase tracking-wider opacity-80">Chatbot persona</div>
                    <div className="font-bold mt-1">{helpCenterResult.theme.chat_welcome_heading || 'Hi 👋'}</div>
                    <div className="text-sm opacity-90">{helpCenterResult.theme.chat_welcome_subheading || ''}</div>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button
                    onClick={() => navigate('/documents')}
                    variant="outline"
                    className="flex-1"
                    data-testid="hc-view-articles-button"
                  >
                    <FileText className="h-4 w-4 mr-2" />View Articles
                  </Button>
                  <Button
                    onClick={() => window.open(helpCenterResult.workspace_url, '_blank')}
                    className="flex-1 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white"
                    data-testid="hc-open-portal-button"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />Open Portal
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

const Stat = ({ label, value }) => (
  <div className="p-3 rounded-xl border border-zinc-200 bg-zinc-50/50">
    <div className="text-2xl font-bold text-zinc-900">{value}</div>
    <div className="text-xs text-zinc-500 mt-0.5">{label}</div>
  </div>
);

const renderSharedFields = (category, setCategory, tags, setTags, categories) => (
  <div className="grid grid-cols-2 gap-4 pt-3 border-t border-zinc-100">
    <div>
      <Label className="text-sm font-medium">Default category (optional)</Label>
      <select
        value={category}
        onChange={(e) => setCategory(e.target.value)}
        className="w-full h-10 px-3 mt-1.5 rounded-md border border-zinc-200 bg-white text-sm"
        data-testid="shared-category-select"
      >
        <option value="">Let AI decide</option>
        {categories.map(cat => (<option key={cat.id} value={cat.name}>{cat.name}</option>))}
      </select>
    </div>
    <div>
      <Label className="text-sm font-medium">Tags (comma-separated)</Label>
      <Input
        value={tags}
        onChange={(e) => setTags(e.target.value)}
        placeholder="e.g. onboarding, billing"
        className="mt-1.5"
        data-testid="shared-tags-input"
      />
    </div>
  </div>
);

export default AIStudio;
